import { useEffect, useState, useRef } from 'react'
import confetti from 'canvas-confetti'
import AnimationLayout from './components/AnimationLayout'

interface Badge {
  name: string
  emoji: string
  description: string
  points_awarded: number
}

interface Props {
  animation: { id: number; name: string; description: string }
  badge: Badge
  all_animations: { id: number; name: string }[]
}

export default function Animation40({ animation, badge, all_animations }: Props) {
  const [phase, setPhase] = useState(0)
  const confettiTriggered = useRef(false)

  useEffect(() => {
    // Phase 1: Apparition avec rotation
    setTimeout(() => setPhase(1), 300)
    // Phase 2: Grossissement avec glow
    setTimeout(() => setPhase(2), 1000)
    // Phase 3: Confettis et texte
    setTimeout(() => setPhase(3), 1800)
    // Phase 4: Célébration finale
    setTimeout(() => setPhase(4), 2500)
  }, [])

  useEffect(() => {
    if (phase === 3 && !confettiTriggered.current) {
      confettiTriggered.current = true

      // Grande explosion de confettis
      const duration = 3000
      const end = Date.now() + duration

      const colors = ['#FFF198', '#fcf5cc', '#feee80', '#0F352D', '#1d5c4d']

      const frame = () => {
        confetti({
          particleCount: 5,
          angle: 60,
          spread: 80,
          origin: { x: 0, y: 0.6 },
          colors
        })
        confetti({
          particleCount: 5,
          angle: 120,
          spread: 80,
          origin: { x: 1, y: 0.6 },
          colors
        })

        if (Date.now() < end) {
          requestAnimationFrame(frame)
        }
      }
      frame()

      // Feux d'artifice
      setTimeout(() => {
        confetti({
          particleCount: 100,
          spread: 100,
          origin: { x: 0.5, y: 0.5 },
          colors
        })
      }, 500)

      setTimeout(() => {
        confetti({
          particleCount: 80,
          spread: 120,
          origin: { x: 0.3, y: 0.4 },
          colors
        })
        confetti({
          particleCount: 80,
          spread: 120,
          origin: { x: 0.7, y: 0.4 },
          colors
        })
      }, 1000)
    }
  }, [phase])

  return (
    <AnimationLayout animation={animation} allAnimations={all_animations}>
      <style>{`
        @keyframes grandEntrance {
          0% {
            transform: scale(0) rotate(-720deg);
            opacity: 0;
          }
          60% {
            transform: scale(1.3) rotate(30deg);
          }
          80% {
            transform: scale(0.9) rotate(-10deg);
          }
          100% {
            transform: scale(1) rotate(0deg);
            opacity: 1;
          }
        }
        @keyframes megaGlow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(255, 241, 152, 0.5),
                        0 0 40px rgba(255, 241, 152, 0.3),
                        0 0 60px rgba(255, 241, 152, 0.2);
          }
          50% {
            box-shadow: 0 0 40px rgba(255, 241, 152, 0.8),
                        0 0 80px rgba(255, 241, 152, 0.5),
                        0 0 120px rgba(255, 241, 152, 0.3);
          }
        }
        @keyframes celebrateBounce {
          0%, 100% { transform: translateY(0) scale(1); }
          25% { transform: translateY(-20px) scale(1.05); }
          50% { transform: translateY(0) scale(0.95); }
          75% { transform: translateY(-10px) scale(1.02); }
        }
        @keyframes textReveal {
          0% {
            transform: translateY(50px) scale(0.5);
            opacity: 0;
          }
          100% {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }
        @keyframes starBurst {
          0% {
            transform: scale(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: scale(2) rotate(180deg);
            opacity: 0;
          }
        }
        .grand-entrance {
          animation: grandEntrance 1s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        .mega-glow {
          animation: megaGlow 1s ease-in-out infinite;
        }
        .celebrate-bounce {
          animation: celebrateBounce 1s ease-in-out infinite;
        }
        .text-reveal {
          animation: textReveal 0.6s ease-out forwards;
        }
        .star-burst {
          animation: starBurst 1s ease-out forwards;
        }
      `}</style>

      <div className="flex flex-col items-center justify-center min-h-[50vh] sm:min-h-[60vh] relative overflow-hidden">
        {/* Étoiles en arrière-plan */}
        {phase >= 3 && Array.from({ length: 12 }, (_, i) => (
          <div
            key={i}
            className="absolute text-4xl star-burst"
            style={{
              left: `${10 + (i % 4) * 25}%`,
              top: `${20 + Math.floor(i / 4) * 30}%`,
              animationDelay: `${i * 0.1}s`
            }}
          >
            ⭐
          </div>
        ))}

        {/* Badge principal */}
        <div className={`relative ${phase >= 1 ? 'grand-entrance' : 'opacity-0'} ${phase >= 2 ? 'mega-glow' : ''} ${phase >= 4 ? 'celebrate-bounce' : ''}`}>
          <div className="w-40 h-40 sm:w-52 sm:h-52 bg-tessan-cream rounded-3xl flex items-center justify-center">
            <span className="text-7xl sm:text-9xl">{badge.emoji}</span>
          </div>

          {/* Rayons de lumière */}
          {phase >= 2 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              {Array.from({ length: 8 }, (_, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-32 bg-gradient-to-t from-tessan-yellow to-transparent origin-bottom"
                  style={{
                    transform: `rotate(${i * 45}deg)`,
                    opacity: 0.6
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Texte avec animation */}
        <div className={`mt-10 text-center ${phase >= 3 ? 'text-reveal' : 'opacity-0'}`}>
          <div className="text-3xl sm:text-5xl font-black text-tessan-yellow mb-2">🎉 {badge.name} 🎉</div>
          <div className="text-xl sm:text-2xl text-tessan-cream font-bold">+{badge.points_awarded} points</div>
        </div>

        <div className={`mt-4 text-center transition-all duration-700 delay-500 ${phase >= 4 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="text-tessan-cream/70 max-w-sm text-lg">{badge.description}</div>
          <div className="mt-6 text-tessan-yellow font-bold text-xl animate-pulse">
            🏆 Félicitations ! 🏆
          </div>
        </div>
      </div>
    </AnimationLayout>
  )
}
